import { PostHogProvider } from '@posthog/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { APIProvider } from '@vis.gl/react-google-maps';
import { StrictMode, type ReactNode } from 'react';
import * as ReactDOM from 'react-dom/client';

import { Sonner } from '@brightlocal/ui-components';

import { patchDomForThirdPartyMutations } from './config/domPatches';
import { ENVIRONMENTS, env } from './config/env';
import { initConsent, initGTM } from './config/gtm';
import { exposePostHog, initPostHog, posthog } from './config/posthog';
import { initSentry } from './config/sentry';
import { GeneralError, NotFound } from './features/errors';
import { routeTree } from './routeTree.gen';
import { queryClient } from './shared/queryClient';
import { GTM } from './shared/utils/gtm';

import './i18n/config';
import './styles.css';
// Workaround: with our Vite + @tailwindcss/postcss setup the font urls inside
// the tokens preset's @import chain end up broken, so the woff2 files never
// reach the build. Importing fonts.css directly lets Vite bundle them. Keep
// this import AFTER styles.css — for duplicate @font-face rules the last one
// wins, and these (working) ones must override the broken copies from the
// preset.
// TODO(FP-80): remove once Tailwind is wired via @tailwindcss/vite https://brightlocal.atlassian.net/browse/FP-80
import '@brightlocal/tokens/fonts.css';

// Prevent browser extensions / Google Translate from crashing React by
// patching DOM methods that throw when a node has been externally moved.
patchDomForThirdPartyMutations();

// Must run before initGTM so consent defaults are established before the GTM
// container fires any tags. Skipped outside production — see config/gtm.ts.
initConsent();
// Bootstraps the GTM data layer and injects the GTM script tag. Runs in all
// environments so the container is available for non-production tag testing.
initGTM();

// Initialize monitoring
initSentry();

// Bootstraps PostHog for feature flags and A/B testing.
initPostHog();

// Non-production flag-debugging console helper. Gated by the runtime
// environment (VITE_ENV), not import.meta.env.DEV — deployed dev/stg/at
// bundles are production builds where DEV is always false.
if (env.environment !== ENVIRONMENTS.PRODUCTION) {
  exposePostHog();
}

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultErrorComponent: GeneralError,
  defaultNotFoundComponent: NotFound
});

// Manually track page views on every completed client-side navigation,
// including hash/query-only changes so back and forward buttons are covered.
router.subscribe('onResolved', ({ toLocation, fromLocation }) => {
  if (fromLocation?.href !== toLocation.href) {
    GTM.pushPageViewEvent(globalThis.window.location.href, document.title);
  }
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const renderApp = (featureMocker: ReactNode = null) =>
  root.render(
    <StrictMode>
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <APIProvider apiKey={env.googleApiKey}>
            <RouterProvider router={router} />
          </APIProvider>
        </QueryClientProvider>
        <Sonner />
        {featureMocker}
      </PostHogProvider>
    </StrictMode>
  );

// Feature Mocker bootstrap for the `local` and `dev` environments. The lib's UI and
// this app's mock catalog are pulled in via dynamic import behind a `VITE_ENV` check,
// so for `stg`/`at`/`prod` builds the gate (and both imports) are statically eliminated
// from the bundle — no mocks ship to prod.
const bootstrapApp = async () => {
  if (import.meta.env.VITE_ENV === ENVIRONMENTS.LOCAL || import.meta.env.VITE_ENV === ENVIRONMENTS.DEVELOPMENT) {
    try {
      const { FeatureMocker, initialHandlersFromStorage, SELECTED_MOCKS_STORAGE_KEY, worker } =
        await import('@brightlocal/feature-mocker');
      const { features, scenarios } = await import('./mocks');

      // Seed previously-selected mocks with `use(...)` BEFORE `start()` (boot hydration).
      const initialHandlers = initialHandlersFromStorage(features, SELECTED_MOCKS_STORAGE_KEY);

      if (initialHandlers.length > 0) {
        worker.use(...initialHandlers);
      }

      // Await `start()` before rendering so first-paint requests are intercepted.
      await worker.start({ onUnhandledRequest: 'bypass' });
      renderApp(<FeatureMocker features={features} scenarios={scenarios} worker={worker} />);
    } catch (error) {
      // A failed mocker boot (bad SW registration, stale mockServiceWorker.js, etc.)
      // must not take the whole app down — fall back to a plain render.
      console.error('Feature Mocker failed to start; rendering without it.', error);
      renderApp();
    }

    return;
  }

  renderApp();
};

void bootstrapApp();
